'use client';
import { useEffect } from 'react';

/*
 * SmoothScroll — Lenis-powered momentum scrolling (Framer-style).
 *
 *   - Wraps native wheel + touch scrolling with an inertia interpolation
 *     so the page glides and decelerates instead of snapping line-by-line.
 *   - Auto-disabled when the user has prefers-reduced-motion set.
 *   - Plays nicely with the scroll-jacked sections (Outcomes / Teams)
 *     because framer-motion's useScroll reads window.scrollY which Lenis
 *     updates each rAF tick.
 *
 * Requires `npm install lenis` (run once in the project root). If the
 * package isn't installed, the dynamic import fails silently and the
 * site falls back to native scrolling — no errors thrown.
 */
export function SmoothScroll() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Honor user preference — skip smooth scroll if reduced motion requested
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return;

    // Skip Lenis on mobile/tablet — continuous rAF hurts TBT/INP in lab + on phones.
    // Native scroll is fine under ~900px; desktop keeps momentum scrolling.
    const mobileOrTablet = window.matchMedia('(max-width: 900px)').matches;
    if (mobileOrTablet) return;

    let lenis;
    let rafId;
    let cancelled = false;

    import('lenis')
      .then(({ default: Lenis }) => {
        if (cancelled) return;
        lenis = new Lenis({
          duration: 1.15,
          easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
          smoothWheel: true,
          smoothTouch: false,
          wheelMultiplier: 1,
          touchMultiplier: 1.5,
        });

        // Expose the instance so scroll-jacked sections can drive
        // programmatic scrolling through Lenis (native window.scrollTo is
        // intercepted/ignored while Lenis is active).
        window.lenis = lenis;

        const raf = (time) => {
          lenis.raf(time);
          rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);
      })
      .catch(() => {
        /* lenis not installed — silently fall back to native scroll */
      });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      if (typeof window !== 'undefined' && window.lenis === lenis) window.lenis = undefined;
      lenis?.destroy();
    };
  }, []);

  return null;
}
